import React, {useState, useCallback} from 'react';
import {View, StyleSheet, FlatList, RefreshControl, ActivityIndicator, Image} from 'react-native';
import {Appbar, Text, Snackbar} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useFocusEffect} from '@react-navigation/native';
import {workerApi} from '../../services/api/workerApi';
import {colors, spacing} from '../../theme';

const TaskAssignmentDetailScreen = ({navigation, route}) => {
  const {workerId, workerName, taskTypeId, taskTypeName} = route.params;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const params = {workerId};
      if (taskTypeId) {
        params.taskTypeId = taskTypeId;
      }
      const response = await workerApi.getTaskAssignments(params);
      setData(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load task details');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [workerId, taskTypeId]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const formatDate = dateStr => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {day: '2-digit', month: 'long', year: 'numeric'});
  };

  const title = taskTypeName ? `${workerName} (${taskTypeName})` : workerName;

  const renderItem = ({item}) => (
    <View style={styles.row}>
      <View style={styles.imageContainer}>
        {item.clothImageUrl ? (
          <Image
            source={{uri: item.clothImageUrl}}
            style={styles.clothImage}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.placeholderImage}>
            <MaterialCommunityIcons name="tshirt-crew-outline" size={48} color={colors.textSecondary} />
          </View>
        )}
        <View style={styles.instanceCodeOverlay}>
          <Text style={styles.instanceCodeText}>{item.instanceCode}</Text>
        </View>
      </View>
      <View style={styles.detailsContainer}>
        <Text style={styles.catalogItemName}>{item.catalogItemName}</Text>
        <Text style={styles.orderNumber}>#{item.orderNumber}</Text>
        {item.customerCity ? (
          <Text style={styles.locationText}>
            Location: <Text style={styles.locationBold}>{item.customerCity}</Text>
          </Text>
        ) : null}
        <Text style={styles.dueDate}>Due On  {formatDate(item.deliveryDate)}</Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark theme={{colors: {onSurface: colors.white, onSurfaceVariant: colors.white}}}>
        <Appbar.BackAction onPress={() => navigation.goBack()} />
        <Appbar.Content
          title={title}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={data}
          keyExtractor={item => String(item.instanceTaskId)}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={() => fetchData(true)} />
          }
          ListEmptyComponent={
            <Text style={styles.emptyText}>No tasks found</Text>
          }
        />
      )}

      <Snackbar
        visible={!!error}
        onDismiss={() => setError('')}
        duration={3000}>
        {error}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.surface},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white, fontSize: 16},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {paddingVertical: spacing.sm},
  row: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  imageContainer: {
    width: 140,
    height: 120,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.background,
  },
  clothImage: {
    width: '100%',
    height: '100%',
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  instanceCodeOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  instanceCodeText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  detailsContainer: {
    flex: 1,
    marginLeft: spacing.md,
    justifyContent: 'center',
  },
  catalogItemName: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4,
  },
  orderNumber: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  locationText: {
    fontSize: 14,
    color: colors.text,
    marginBottom: 4,
  },
  locationBold: {
    fontWeight: '700',
  },
  dueDate: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  separator: {
    height: 1,
    backgroundColor: colors.divider,
    marginHorizontal: spacing.md,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    fontSize: 16,
  },
});

export default TaskAssignmentDetailScreen;
